import React, { useCallback, useMemo, useRef, useState } from 'react';
import StudentIDCardTemplate from './id-card/StudentIDCardTemplate';
import IdCardTemplateSelector from './settings/IdCardTemplateSelector';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Image,
  PenLine,
  Upload,
  IdCard,
  QrCode,
  Palette,
  Type,
  Loader2,
} from 'lucide-react';
import { parseIdCardTemplate } from '../utils/idCardFormats';

const PREVIEW_STUDENT = {
  id: 'preview',
  first_name: 'Aarav',
  last_name: 'Sharma',
  admission_no: 'REG-2026-001',
  roll_no: 'STU-1001',
  guardian_name: 'R. Sharma',
  guardian_phone: '9876543210',
  class_name: 'Class X',
  section_name: 'A',
  admission_date: '2025-04-01',
};

function UploadField({
  icon: Icon,
  title,
  hint,
  uploading,
  onPick,
  previewUrl,
  previewClass = 'h-16 max-w-full object-contain rounded-lg border border-border bg-background p-2',
}) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 hover:border-blue-300/60 hover:bg-blue-50/30 dark:hover:bg-blue-950/10 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
          </div>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={onPick} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-border hover:border-blue-300 hover:bg-background"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload image
              </>
            )}
          </Button>
          {previewUrl ? (
            <img
              key={previewUrl}
              src={previewUrl}
              alt={title}
              className={previewClass}
              onError={(e) => {
                e.currentTarget.style.opacity = '0.35';
              }}
            />
          ) : (
            <div className="h-14 rounded-lg border border-border/80 bg-background/80 flex items-center justify-center text-xs text-muted-foreground">
              No file selected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5">
      <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground truncate">{value}</p>
      </div>
    </div>
  );
}

export default function IdCardSetupPanel({
  schoolDetails,
  setSchoolDetails,
  currentSchoolId,
  getTilePreviewUrl,
  cardAssetOverrides,
  previewVersion,
  uploadHandlers,
  uploading,
  onSave,
  saving,
  loading = false,
  idCardTemplates = [],
  activeIdCardTemplateId = '',
  idCardTemplateSaving = false,
  idCardTemplatesLoading = false,
  onSaveIdCardTemplate,
}) {
  const [draftTemplate, setDraftTemplate] = useState(null);
  const handleDraftChange = useCallback((tpl) => setDraftTemplate(tpl), []);

  const previewTemplate = useMemo(
    () => parseIdCardTemplate(draftTemplate),
    [draftTemplate],
  );

  const schoolForPreview = useMemo(
    () => (schoolDetails ? { ...schoolDetails, id: currentSchoolId } : null),
    [schoolDetails, currentSchoolId],
  );

  const previewStudent = useMemo(
    () => ({ ...PREVIEW_STUDENT, school_id: currentSchoolId }),
    [currentSchoolId],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!schoolDetails) {
    return (
      <Card className="rounded-2xl border-border">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Select a school to configure ID cards.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(300px,420px)]">
      <div className="space-y-5">
        <Card className="rounded-2xl border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Branding &amp; assets</CardTitle>
            <CardDescription>
              School header logo comes from School Information. Back card uses app product logo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UploadField
              icon={PenLine}
              title="Principal signature"
              hint="PNG transparent · shown above footer on front card"
              uploading={uploading.signature}
              onPick={uploadHandlers.signature}
              previewUrl={getTilePreviewUrl('principalSignature')}
              previewClass="h-12 max-w-[180px] object-contain rounded-lg border border-border bg-background p-1"
            />
            <UploadField
              icon={Image}
              title="Footer building image"
              hint="Full-width strip above address bar"
              uploading={uploading.footer}
              onPick={uploadHandlers.footer}
              previewUrl={getTilePreviewUrl('footerBackground')}
              previewClass="h-16 w-full object-cover rounded-lg border border-border"
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Card details</CardTitle>
            <CardDescription>Text shown on the back of every student ID card.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Website</Label>
              <Input
                value={schoolDetails.website || ''}
                onChange={(e) => setSchoolDetails({ ...schoolDetails, website: e.target.value })}
                className="mt-1.5 rounded-lg border-border focus-visible:ring-blue-500/30"
                placeholder="www.yourschool.edu"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Managing organization name</Label>
              <Input
                value={schoolDetails.id_card_managed_org_name || ''}
                onChange={(e) =>
                  setSchoolDetails({ ...schoolDetails, id_card_managed_org_name: e.target.value })
                }
                className="mt-1.5 rounded-lg border-border focus-visible:ring-blue-500/30"
                placeholder="CP Rawat Softwares"
              />
            </div>
            <Button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              {saving ? 'Saving…' : 'Save card settings'}
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Card template</CardTitle>
            <CardDescription>
              Choose the school-wide ID card design and orientation. Save to apply to all ID card generation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {idCardTemplatesLoading && !idCardTemplates.length ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading formats…
              </p>
            ) : idCardTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No ID card formats available. Run database migrations.</p>
            ) : (
              <IdCardTemplateSelector
                templates={idCardTemplates}
                activeTemplateId={activeIdCardTemplateId}
                saving={idCardTemplateSaving}
                onSave={onSaveIdCardTemplate}
                onDraftChange={handleDraftChange}
              />
            )}
            <div className="grid gap-2 sm:grid-cols-2 pt-2 border-t border-border">
              <InfoRow icon={Palette} label="Primary color" value="Saffron orange (#FF6B00)" />
              <InfoRow icon={Type} label="Typography" value="Inter / system sans" />
              <InfoRow icon={QrCode} label="QR code" value="Enabled on back card" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="xl:sticky xl:top-4 h-fit">
        <Card className="rounded-2xl border-border shadow-md overflow-hidden">
          <CardHeader className="pb-2 border-b border-border bg-muted/20">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <IdCard className="h-4 w-4 text-blue-600 dark:text-blue-400" /> Live preview
            </CardTitle>
            <CardDescription>Updates instantly as you upload, edit fields, or change format.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 bg-muted/40">
            <div className="rounded-xl bg-gradient-to-b from-muted/80 to-muted/40 p-4 sm:p-6 flex justify-center min-h-[320px]">
              <StudentIDCardTemplate
                compact
                format={previewTemplate.format}
                design={previewTemplate.design}
                student={previewStudent}
                school={schoolForPreview}
                photoUrl={null}
                assetOverrides={cardAssetOverrides}
                previewVersion={previewVersion}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
